import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { emit, events } from '@estee/elc-service-bus';
import { TriggerType } from '@estee/elc-logging';
import { ComponentRenderer, ComponentNodeType } from './renderers/ComponentRenderer';
import { RenderStrategies } from './renderers/LazyRenderer';
import { PerformanceMarker } from '@estee/elc-telemetry';
import { root } from '@estee/elc-universal-utils';

const COMPONENT_ATTRIBUTE = 'data-component';
const COMPONENT_SELECTOR = `[${COMPONENT_ATTRIBUTE}]`;

const { name: serviceName } = __serviceInfo__;

type PrimitiveTypes = number | boolean | object | null | string;
interface IComponentLoaded {
    shouldRender: boolean;
    config: PrimitiveTypes | PrimitiveTypes[];
}

interface IComponentsLoaded {
    [key: string]: IComponentLoaded[];
}

interface IRenderParams {
    component: React.ReactElement;
    node: HTMLElement;
    name: string;
}

function isNodeProductFull(node: HTMLElement) {
    return node.getAttribute(COMPONENT_ATTRIBUTE) === 'product-full';
}
function isNodeProductGrid(node: HTMLElement) {
    return node.getAttribute(COMPONENT_ATTRIBUTE) === 'product-grid';
}

const MAIN_VIEW_ATTRIBUTE = 'data-main-view';

export class BrowserComponentRenderer extends ComponentRenderer {
    private nodesToRenderList: Set<HTMLElement> = new Set([]);
    private isRenderReadyPromise: Promise<void>;
    private renderedNodes: string[] = [];
    protected readonly performanceMarker = new PerformanceMarker(this.logger, serviceName);

    public isRenderReady = () => this.isRenderReadyPromise;

    protected getNodeAttribute = (node: HTMLElement, name: string) => node.getAttribute(name);

    protected isServiceViewNode = (element: HTMLElement): boolean =>
        element.matches(COMPONENT_SELECTOR);

    protected getServiceViewsFromNode = (node: HTMLElement) =>
        node.querySelectorAll(COMPONENT_SELECTOR) as NodeListOf<HTMLElement>;

    private isElement = (node: Node) => node.nodeType === Node.ELEMENT_NODE;

    private addNodeToRenderList = (nodes: Set<HTMLElement>, nodeToAdd: HTMLElement) => {
        if (!this.nodesToRenderList.has(nodeToAdd)) {
            nodes.add(nodeToAdd);
            this.nodesToRenderList.add(nodeToAdd);
        }
    };

    private removeNodeFromRenderList = (nodeToRemove: HTMLElement) => {
        if (this.nodesToRenderList.has(nodeToRemove)) {
            this.nodesToRenderList.delete(nodeToRemove);
        }
    };

    private processMutationRecord = (nodes: Set<HTMLElement>, record: MutationRecord) => {
        record.addedNodes.forEach((node: HTMLElement) => {
            if (this.isElement(node)) {
                const serviceViews = this.isServiceViewNode(node)
                    ? [node]
                    : this.getServiceViewsFromNode(node);

                serviceViews.forEach((view: HTMLElement) => this.addNodeToRenderList(nodes, view));
            }
        });

        // If a ServiceView is removed from DOM we need to remove also the reference to it
        record.removedNodes.forEach(this.removeNodeFromRenderList);

        return nodes;
    };

    private onHTMLChanged = async (records: MutationRecord[]) => {
        const addedNodes = records.reduce(this.processMutationRecord, new Set<HTMLElement>());

        await this.renderReactNodes([...addedNodes]);
    };

    private createMutationObserver = (callback: MutationCallback) => {
        if (typeof MutationObserver !== 'undefined') {
            return new MutationObserver(callback);
        }

        return null;
    };

    private watchRenderOutput() {
        const mutationObserverComponent = this.createMutationObserver(this.onHTMLChanged);
        if (mutationObserverComponent) {
            mutationObserverComponent.observe(document.body, {
                subtree: true,
                childList: true
            });
        }
    }

    private onReactNodeChange = (
        node: HTMLElement,
        markerName: string
    ) => (records: MutationRecord[], observer: MutationObserver) => {
        records.some((record: MutationRecord) => {
            if (record.addedNodes.length > 0) {
                const perfMeasure = this.performanceMarker.endMark(markerName);
                observer?.disconnect();

                try {
                    if (node.getAttribute(MAIN_VIEW_ATTRIBUTE)) {
                        const timerName = isNodeProductFull(node)
                            ? 'Full'
                            : 'Grid';
                        (root as Window).mPulse.sendTimers({
                            [`PROD${timerName}Load`]: performance.now(),
                            [`PROD${timerName}Render`]: perfMeasure?.duration || 0
                        });
                    }
                } catch (error) {
                    this.logger.error({
                        triggerType: TriggerType.other,
                        message: error.message,
                        payload: {
                            error
                        }
                    });
                }

                return true;
            }

            return false;
        });
    };

    private render = ({ component, node, name }: IRenderParams) => {
        const counterSameView = this.renderedNodes.filter((_name) => _name === name).length;

        this.renderedNodes.push(name);

        const renderMark = `view_${name}`.concat(counterSameView ? `_${counterSameView}` : '');
        const firstRenderMark = `first_view_${name}`.concat(
            counterSameView ? `_${counterSameView}` : ''
        );

        this.performanceMarker.startMark(renderMark);
        this.performanceMarker.startMark(firstRenderMark);

        return new Promise<void>((resolve, reject) => {
            try {
                ReactDOM.render(component, node, () => {
                    this.performanceMarker.endMark(firstRenderMark);

                    if (name.includes('search-gnav-input-field')) {
                        resolve();

                        return ;
                    }

                    const mutationObserver = this.createMutationObserver(
                        this.onReactNodeChange(node, renderMark)
                    );
                    if (mutationObserver) {
                        mutationObserver.observe(node, {
                            childList: true
                        });
                    }
                    resolve();
                });
            } catch (error) {
                this.logger.error({
                    message: `Failed to render view ${name}`,
                    triggerType: TriggerType.render,
                    payload: {
                        error
                    }
                });
                this.performanceMarker.endMark(renderMark);
                reject(node);
            }
        });
    };

    private renderNode = async (node: HTMLElement) => {
        try {
            const componentNode = this.convertNodeToComponentNodeType(node);
            const nodeToRender: ComponentNodeType = await this.getComponentForNode(componentNode);

            if (nodeToRender.component) {
                const { component, name } = nodeToRender;

                const renderPromise = this.render({
                    component,
                    node: nodeToRender.node,
                    name
                });

                return renderPromise;
            }
        } catch (error) {
            this.logger.error({
                triggerType: TriggerType.render,
                message: `Error trying to render node: ${node?.getAttribute(COMPONENT_ATTRIBUTE)}`,
                payload: {
                    error
                }
            });
        }

        return Promise.resolve(node);
    };

    private renderReactNodes = async (nodes: Element[]) => {
        try {
            const renderedNodes = (await Promise.all(
                nodes.map(this.renderNode)
            )) as (null | HTMLElement)[];

            const pendingNodes = renderedNodes.filter(Boolean) as Element[];

            if (pendingNodes.length > 0) {
                this.setTimeoutRenderingNodes(pendingNodes);
            }
        } catch (error) {
            this.logger.error({
                triggerType: TriggerType.render,
                message: 'Error trying to render nodes',
                payload: {
                    error
                }
            });
        } finally {
            return Promise.resolve();
        }
    };

    private setTimeoutRenderingNodes = async (nodes: Element[]) => {
        setTimeout(async () => {
            await this.renderReactNodes(nodes);
        }, 250);
    };

    private renderLazyNode = (node: HTMLElement) => {
        if (!this.nodesToRenderList.has(node)) {
            this.nodesToRenderList.add(node);
            this.renderReactNodes([node]);
        }
    };

    private setMainViews = (nodes: HTMLElement[]) => {
        const productFullNode = nodes.find(isNodeProductFull);
        const firstProductGridNode = nodes.find(isNodeProductGrid);

        if (productFullNode || firstProductGridNode) {
            // If SPP page then render first the product-full
            if (productFullNode) {
                productFullNode.setAttribute(this.renderMethodAttribute, RenderStrategies.default);
                productFullNode.setAttribute(MAIN_VIEW_ATTRIBUTE, 'true');
            // If MPP page then render first the product-grid
            } else if (firstProductGridNode) {
                nodes.forEach(node => {
                    node.setAttribute(this.renderMethodAttribute, RenderStrategies.ondemand);
                });
                firstProductGridNode.setAttribute(
                    this.renderMethodAttribute, RenderStrategies.default
                );
                firstProductGridNode.setAttribute(MAIN_VIEW_ATTRIBUTE, 'true');
            }
        }
    };

    private processMountPoints = async () => {
        const allNodes: HTMLElement[] = Array.from(document.querySelectorAll(COMPONENT_SELECTOR));
        this.setMainViews(allNodes);

        const toRenderNodes = allNodes.filter(this.shouldRenderNode).sort(
            (viewA, viewB) =>
                viewA.getAttribute(MAIN_VIEW_ATTRIBUTE) ? -1 : 1
        );
        this.nodesToRenderList = new Set(toRenderNodes);

        await this.renderReactNodes(toRenderNodes);

        if (toRenderNodes.length < allNodes.length) {
            this.initLazyRenderer(this.renderLazyNode);
        }

        this.announceMountPoints([...allNodes]);
    };

    private announceMountPoints = (nodes: HTMLElement[]) => {
        const loadedComponents = nodes.reduce(
            (components: IComponentsLoaded, node: HTMLElement) => {
                try {
                    const { component, config } = node.dataset;

                    if (component) {
                        components[component] = components[component] || [];

                        components[component].push({
                            shouldRender: this.nodesToRenderList.has(node),
                            config: this.safeJSONParse<{}>(config, {})
                        });
                    }
                } catch (error) {
                    this.logger.error({
                        triggerType: TriggerType.render,
                        message: 'Failed parsing config',
                        payload: {
                            error
                        }
                    });
                }

                return components;
            },
            {} as IComponentsLoaded
        );

        emit(events.COMPONENTS_LOADED, loadedComponents);
    };

    private onDOMContentLoaded = () => {
        this.isRenderReadyPromise = this.processMountPoints();
        this.watchRenderOutput();
    };

    public init = async () => {
        const initMark = 'init_component_renderer';
        this.performanceMarker.startMark(initMark);
        const isDocumentReady = ['complete', 'interactive'].includes(document.readyState);

        if (isDocumentReady) {
            this.performanceMarker.endMark(initMark);
            this.onDOMContentLoaded();
        } else {
            await new Promise<void>((resolve) =>
                document.addEventListener('DOMContentLoaded', () => {
                    this.performanceMarker.endMark(initMark);
                    this.onDOMContentLoaded();
                    resolve();
                })
            );
        }

        await this.isRenderReady();
    };
}
